import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Timeline from "@mui/lab/Timeline";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import AssessmentTwoToneIcon from "@mui/icons-material/AssessmentTwoTone";
import DateItem from "./DateItem";
import EventItem from "./EventItem";
import importTimeline, {
  DAY_ITEM,
  EVENT_ITEM,
  GROUP_BY_SURVEY,
  GROUP_BY_TIME,
} from "./importTimeline";
import FilterTimeline from "./FilterTimeline";
import useTimeline from "./useTimeline";
import LoadingTimeline from "./LoadingTimeline";
import EmptyItem from "../Components/EmptyItem";
import useBeeps from "./useBeeps";
import useNotes from "./Note/useNotes";
import useAccountSettings from "../ManageAccount/useAccountSettings";

function TimelineItem(props) {
  switch (props.type) {
    case DAY_ITEM:
      return <DateItem {...props} />;
    case EVENT_ITEM:
      return <EventItem {...props} />;
    default:
      return "";
  }
}

const maxItemsInGroupByTimeView = 10;

export default function UserTimeline({ page, handleClearPage }) {
  const { t } = useTranslation();
  // local state
  const [filterOnView, setFilterOnView] = useState(undefined); // filter the timeline

  const initTimeline = (view) => {
    // filter the timeline
    setFilterOnView(view);
    // reset the infinite scroll
    handleClearPage();
  };

  // server state
  const { accountSettings } = useAccountSettings();
  const { notes, isNotesLoading } = useNotes();
  const { timeline, isTimelineLoading } = useTimeline();
  const groupBy = filterOnView
    ? filterOnView
    : timeline?.events?.length > maxItemsInGroupByTimeView
    ? GROUP_BY_SURVEY
    : GROUP_BY_TIME; // default is based on the amount of timeline items. Few items => group by time. More items => group by survey
  const importedTimeline =
    timeline?.events && notes
      ? importTimeline({
          type: groupBy,
          payload: { events: timeline.events, notes: notes },
        })
      : [];
  const current = [...importedTimeline].slice(0, 15 * page);

  const prefetchBeepIds =
    current?.[1]?.surveys.map((survey) => survey.id) ?? [];
  const { isBeepLoading } = useBeeps(
    prefetchBeepIds,
    prefetchBeepIds.length > 0
  );

  // Render the timeline
  if (timeline?.status === 0 || timeline?.status === -1)
    return <Typography>{t("common.noAccess")}</Typography>;
  if (
    isTimelineLoading ||
    isNotesLoading ||
    (prefetchBeepIds.length > 0 && isBeepLoading)
  )
    return <LoadingTimeline filterOnView={groupBy} />;
  return (
    <div>
      <Box display="flex" justifyContent={"space-between"}>
        <Typography variant="h6">{t("timeline.subtitle")}</Typography>
        <FilterTimeline filterOnView={groupBy} initTimeline={initTimeline} />
      </Box>
      <Divider />
      {current?.length > 0 ? (
        <Timeline
          align="left"
          sx={{ paddingTop: 3, paddingBottom: 9, width: "100%" }}
        >
          {current.map((creation) => (
            <TimelineItem
              key={creation.type + creation.event.id}
              {...creation}
              filterOnView={groupBy}
              carers={timeline?.carers}
              practitionerCode={accountSettings.code}
            />
          ))}
        </Timeline>
      ) : (
        <EmptyItem
          text={t("timeline.noActivity")}
          Icon={AssessmentTwoToneIcon}
        />
      )}
    </div>
  );
}
